import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { PATH_TENANTS } from '../../routes/paths';
import apiClient from '../../config/apiClient';

interface Tenant {
  id: string;
  name: string;
  description: string;
  threshold: number;
  application: {
    name: string;
    clientId: string;
  } | null;
}

const TenantTable: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTenants = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(`/tenants`);
        setTenants(response.data);
      } catch (error) {
        setError('Error fetching tenants');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTenants();
  }, []);

  const handleDelete = async () => {
    if (!tenantToDelete) return;

    try {
      await apiClient.delete(`/tenant/${tenantToDelete}`);
      setTenants((prev) => prev.filter((tenant) => tenant.id !== tenantToDelete));
      setOpen(false);
    } catch (error) {
      console.error('Error deleting tenant:', error);
    }
  };

  const handleOpenDeleteModal = (id: string) => {
    setTenantToDelete(id);
    setOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setOpen(false);
    setTenantToDelete(null);
  };

  const handleAddTenant = () => {
    navigate('/tenants/create');
  };

  const handleEditTenant = (id: string) => {
    navigate(PATH_TENANTS.general.edit(id));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1200px', margin: '0 auto', mt: 5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Tenants</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddTenant}
        >
          Add Tenant
        </Button>
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Tenant Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Threshold</TableCell>
            <TableCell>Org ID</TableCell>
            <TableCell>Application ID</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tenants.map((tenant) => (
            <TableRow key={tenant.id}>
              <TableCell>{tenant.id}</TableCell>
              <TableCell>{tenant.name}</TableCell>
              <TableCell>{tenant.description}</TableCell>
              <TableCell>{tenant.threshold}</TableCell>
              <TableCell>{tenant.application?.name}</TableCell>
              <TableCell>{tenant.application?.clientId}</TableCell>
              <TableCell align="right">
                <IconButton
                  color="primary"
                  onClick={() => handleOpenDeleteModal(tenant.id)}
                >
                  <DeleteIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleEditTenant(tenant.id)}
                >
                  <EditIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog
        open={open}
        onClose={handleCloseDeleteModal}
      >
        <DialogTitle>Delete Tenant</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this tenant? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="primary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantTable;
